'use client'

import * as React from 'react'
import { cn } from '@cocoonspace/ui/lib/utils'
import { Drawer as DrawerPrimitive } from 'vaul'

const Drawer = ({
	shouldScaleBackground = true,
	...props
}: React.ComponentProps<typeof DrawerPrimitive.Root>) => (
	<DrawerPrimitive.Root
		shouldScaleBackground={shouldScaleBackground}
		{...props}
	/>
)

Drawer.displayName = 'Drawer'

const DrawerTrigger: React.ComponentType<
	React.ComponentProps<typeof DrawerPrimitive.Trigger>
> = DrawerPrimitive.Trigger
const DrawerPortal = DrawerPrimitive.Portal
const DrawerClose: React.ComponentType<
	React.ComponentProps<typeof DrawerPrimitive.Close>
> = DrawerPrimitive.Close

const DrawerOverlay: React.ForwardRefExoticComponent<
	React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Overlay> &
		React.RefAttributes<React.ElementRef<typeof DrawerPrimitive.Overlay>>
> = React.forwardRef<
	React.ElementRef<typeof DrawerPrimitive.Overlay>,
	React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Overlay>
>(({ className, ...props }, ref) => (
	<DrawerPrimitive.Overlay
		className={cn('fixed inset-0 z-50 bg-black/80', className)}
		ref={ref}
		{...props}
	/>
))

DrawerOverlay.displayName = DrawerPrimitive.Overlay.displayName

const DrawerContent: React.ForwardRefExoticComponent<
	React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Content> &
		React.RefAttributes<React.ElementRef<typeof DrawerPrimitive.Content>>
> = React.forwardRef<
	React.ElementRef<typeof DrawerPrimitive.Content>,
	React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Content>
>(({ className, children, ...props }, ref) => (
	<DrawerPortal>
		<DrawerOverlay />

		<DrawerPrimitive.Content
			className={cn(
				'bg-background fixed inset-x-0 bottom-0 z-50 mt-24 flex h-auto flex-col rounded-t-[10px] border p-4',
				className,
			)}
			ref={ref}
			{...props}
		>
			<div className='bg-muted mx-auto my-2 h-1 w-[100px] rounded-full' />

			{children}
		</DrawerPrimitive.Content>
	</DrawerPortal>
))

DrawerContent.displayName = 'DrawerContent'

const DrawerHeader = ({
	className,
	...props
}: React.HTMLAttributes<HTMLDivElement>) => (
	<div
		className={cn('grid gap-1.5 p-4 text-center sm:text-left', className)}
		{...props}
	/>
)

DrawerHeader.displayName = 'DrawerHeader'

const DrawerFooter = ({
	className,
	...props
}: React.HTMLAttributes<HTMLDivElement>) => (
	<div
		className={cn('mt-auto flex flex-col gap-2 p-4', className)}
		{...props}
	/>
)

DrawerFooter.displayName = 'DrawerFooter'

const DrawerTitle: React.ForwardRefExoticComponent<
	React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Title> &
		React.RefAttributes<React.ElementRef<typeof DrawerPrimitive.Title>>
> = React.forwardRef<
	React.ElementRef<typeof DrawerPrimitive.Title>,
	React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Title>
>(({ className, ...props }, ref) => (
	<DrawerPrimitive.Title
		className={cn(
			'text-lg leading-none font-semibold tracking-tight',
			className,
		)}
		ref={ref}
		{...props}
	/>
))

DrawerTitle.displayName = DrawerPrimitive.Title.displayName

const DrawerDescription: React.ForwardRefExoticComponent<
	React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Description> &
		React.RefAttributes<React.ElementRef<typeof DrawerPrimitive.Description>>
> = React.forwardRef<
	React.ElementRef<typeof DrawerPrimitive.Description>,
	React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Description>
>(({ className, ...props }, ref) => (
	<DrawerPrimitive.Description
		className={cn('text-foreground/80 text-sm', className)}
		ref={ref}
		{...props}
	/>
))

DrawerDescription.displayName = DrawerPrimitive.Description.displayName

export {
	Drawer,
	DrawerClose,
	DrawerContent,
	DrawerDescription,
	DrawerFooter,
	DrawerHeader,
	DrawerOverlay,
	DrawerPortal,
	DrawerTitle,
	DrawerTrigger,
}
